/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useParams, useSearchParams } from "react-router-dom";
import {
    Card,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Pagination,
    Table,
} from "@jhool-io/fe-components";
import ListState from "../../../../../components/ListState/ListState";
import Loader from "../../../../../shared-ui/Loader/Loader";
import { useFetchClientCallHistory } from "../../../hooks/clients.queries";
import { useDoesUserHavePermission } from "../../../../../hooks/permissions";
import { UserPermisions } from "../../../../../utils/types/user";
import {
    CallType,
    CommunicationDirection,
    CommunicationNoteChannel,
    IClientCallLog,
} from "../../../types/clients.types";
import { formatDate } from "../../../../../utils/helpers";
import {
    COMMUNICATION_HISTORY_DATE_FORMAT,
    LISTS_DEFAULT_LIMIT,
} from "../../../../../utils/constants";
import CommunicationPhoneLink from "../CommunicationPhoneLink/CommunicationPhoneLink";
import {
    getRingCentralUrl,
    formatCallDuration,
} from "../../../helpers/clients.helpers";
import CommunicationDirectionIcon from "../CommunicationDirectionIcon/CommunicationDirectionIcon";
import CommunicationNotes from "../CommunicationNotes/CommunicationNotes";
import CommunicationNotesCell from "../CommunicationNotesCell/CommunicationNotesCell";

export default function CallHistoryList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const params = useParams();
    const clientId = params.clientId as string;

    const pageFilter = Number(searchParams.get("call_page")) || 1;
    const limitFilter =
        Number(searchParams.get("call_limit")) || LISTS_DEFAULT_LIMIT;
    const directionFilter = searchParams.get("call_direction") ?? "";
    const callTypeFilter = searchParams.get("call_type") ?? "";
    const notesRecordId = searchParams.get("call_notes_record_id");

    const canAddNote = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_COMMUNICATIONS_WRITE,
    ]);

    const { data, isLoading, error, isSuccess } = useFetchClientCallHistory(
        clientId,
        {
            page: pageFilter,
            limit: limitFilter,
            direction: directionFilter,
            call_type: callTypeFilter,
        }
    );

    const recordWithNotesInView = data?.data.find(
        (record) => record.call_id === notesRecordId
    );

    const handleFilterChange = (paramName: string, value: string) => {
        if (value === "All") searchParams.delete(paramName);
        else searchParams.set(paramName, value);
        searchParams.delete("call_page");
        setSearchParams(searchParams);
    };

    const handlePageChange = (page: number) => {
        searchParams.set("call_page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("call_limit", String(limit));
        setSearchParams(searchParams);
    };

    const handleCloseNotes = () => {
        searchParams.delete("call_notes_record_id");
        setSearchParams(searchParams);
    };

    const getClientNumber = (row: Row<IClientCallLog>) => {
        if (row.original.direction === CommunicationDirection.INBOUND) {
            return row.original.from;
        }
        if (row.original.direction === CommunicationDirection.OUTBOUND) {
            return row.original.to;
        }
        return null;
    };

    const handleRowClick = (row: Row<IClientCallLog>) => {
        const clientNumber = getClientNumber(row);
        if (!clientNumber) return;

        const numberHistoryUrl = getRingCentralUrl(
            clientNumber,
            "call-history"
        );
        if (numberHistoryUrl) {
            window.open(numberHistoryUrl, "_blank", "noopener,noreferrer");
        }
    };

    const columns: ColumnDef<IClientCallLog>[] = React.useMemo(
        () => [
            {
                accessorKey: "started_at",
                header: "Date",
                cell: ({ row }) => (
                    <div className="flex items-center gap-x-2">
                        <CommunicationDirectionIcon
                            direction={row.original.direction}
                        />
                        <span>
                            {row.original.started_at
                                ? formatDate(
                                      row.original.started_at,
                                      false,
                                      COMMUNICATION_HISTORY_DATE_FORMAT
                                  )
                                : "--"}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: "from",
                header: "Sender",
                cell: ({ row }) => (
                    <CommunicationPhoneLink value={row.original.from} />
                ),
            },
            {
                accessorKey: "to",
                header: "Recipient",
                cell: ({ row }) => (
                    <CommunicationPhoneLink value={row.original.to} />
                ),
            },
            {
                accessorKey: "duration_seconds",
                header: "Duration",
                cell: ({ row }) =>
                    formatCallDuration(row.original.duration_seconds),
            },
            {
                accessorKey: "result",
                header: "Result",
                cell: ({ row }) => row.original.result || "--",
            },
            {
                accessorKey: "notes",
                header: "Notes",
                cell: ({ row }) => (
                    <CommunicationNotesCell
                        notesCount={row.original.notes?.length || 0}
                        canAddNote={canAddNote}
                        onClick={() => {
                            searchParams.set(
                                "call_notes_record_id",
                                row.original.call_id
                            );
                            setSearchParams(searchParams);
                        }}
                    />
                ),
            },
        ],
        [canAddNote, searchParams, setSearchParams]
    );

    return (
        <div>
            {recordWithNotesInView ? (
                <CommunicationNotes
                    channel={CommunicationNoteChannel.CALL}
                    recordId={recordWithNotesInView.call_id}
                    notes={recordWithNotesInView.notes || []}
                    canAddNote={canAddNote}
                    onClose={handleCloseNotes}
                />
            ) : null}
            <Card classNames="mb-[--spacing-header]">
                <div className="flex items-center gap-[--spacing-items] flex-wrap">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                {directionFilter || "Direction"}
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="150px">
                            {[
                                "All",
                                ...Object.values(CommunicationDirection),
                            ].map((direction) => (
                                <DropdownItem
                                    key={direction}
                                    onClick={() =>
                                        handleFilterChange(
                                            "call_direction",
                                            direction
                                        )
                                    }
                                >
                                    {direction}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                {callTypeFilter || "Call type"}
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="150px">
                            {["All", ...Object.values(CallType)].map(
                                (callType) => (
                                    <DropdownItem
                                        key={callType}
                                        onClick={() =>
                                            handleFilterChange(
                                                "call_type",
                                                callType
                                            )
                                        }
                                    >
                                        {callType}
                                    </DropdownItem>
                                )
                            )}
                        </DropdownContent>
                    </Dropdown>
                </div>
            </Card>
            {isLoading && <Loader />}
            {error && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error.response?.data.message ??
                        "Cannot display call history at this time"
                    }
                />
            )}
            {isSuccess && data.data.length === 0 && (
                <ListState
                    isError={false}
                    stateHelperText="Calls will appear here once communication begins"
                    emptyMessage="No calls to display"
                />
            )}
            {isSuccess && data.data.length > 0 && (
                <>
                    <Table
                        columns={columns}
                        data={data.data}
                        handleRowClick={handleRowClick}
                    />
                    <Pagination
                        totalCount={data.total_count}
                        totalPages={data.total_pages}
                        currentPage={pageFilter}
                        onPageChange={handlePageChange}
                        limit={limitFilter}
                        onLimitChange={handleLimitChange}
                    />
                </>
            )}
        </div>
    );
}
